// Finance build — the Income Overview per-loan drill at
// /portfolio-admin/income-overview/loans/:loanId. Reads the loanId param and
// carries the window params. Renders:
//   (1) the loan's income-sources split (the reused L2 IncomeOutflowPanel),
//   (2) a per-loan "Income events" list with the commissions each event
//       generated,
//   (3) a back link to the overview + a cross-link to the loan's full
//       Financials tab (?tab=financials).
// READ-ONLY.

import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader, Skeleton } from "@/design-system/components/ui";
import { IncomeOutflowPanel } from "@/components/portfolioAdmin/loanDetail/financials/IncomeOutflowPanel";
import { IncomeEventsList } from "@/components/portfolioAdmin/finance/incomeOverview/IncomeEventsList";
import {
  readIncomeOverviewParams,
  rangeSearch,
} from "@/components/portfolioAdmin/finance/incomeOverview/range";
import { useLoanIncomeEvents } from "@/hooks/portfolioAdmin/useIncomeOverview";

export default function IncomeOverviewLoanPage() {
  const { loanId } = useParams<{ loanId: string }>();
  const [params] = useSearchParams();

  const queryParams = useMemo(
    () => readIncomeOverviewParams(params),
    [params],
  );
  const search = rangeSearch(queryParams);

  const query = useLoanIncomeEvents(loanId, queryParams);
  const data = query.data;
  const loanNumber = data?.loanNumber;

  return (
    <div className="flex flex-col gap-space-5" data-testid="income-overview-loan-page">
      <div>
        <Link
          to={`/portfolio-admin/income-overview${search}`}
          className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
          data-testid="income-overview-loan-back"
        >
          <ArrowLeft size={14} strokeWidth={1.5} aria-hidden />
          Back to Income Overview
        </Link>
      </div>

      <PageHeader
        eyebrow="Loan financials"
        title={loanNumber ? `Income — ${loanNumber}` : "Loan income"}
        description="This loan's recognized income: its income-sources split and every income event with the commissions it generated."
        actions={
          loanId ? (
            <Link
              to={`/portfolio-admin/loans/${loanId}?tab=financials`}
              className="text-small text-gold-shadow hover:underline"
              data-testid="income-overview-loan-financials-link"
            >
              Open full Financials tab
            </Link>
          ) : undefined
        }
      />

      {loanId && <IncomeOutflowPanel loanId={loanId} />}

      {query.isError ? (
        <div
          className="rounded-lg border border-status-error bg-status-error/12 px-space-4 py-space-3 text-small text-status-error"
          data-testid="income-overview-loan-error"
        >
          Couldn&apos;t load this loan&apos;s income events. Try again.
        </div>
      ) : query.isLoading || !data ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <IncomeEventsList events={data.events} />
      )}
    </div>
  );
}
